import { ReactElement, useRef } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import { ChartMarkerStyle } from '@common/api';
import { Button } from '@components/button/Button';
import { IsolateAbsolute, Relative } from '@components/layout/Flex';
import { Line, Dot, Circle, Square } from '@components/icons/FarseerIcons';
import { ContextMenu, useContextMenuState } from '@components/context-menu/ContextMenu';

export type MarkerStylePickerProps = {
    value: ChartMarkerStyle;
    disabled?: boolean;
    onChange?: (type: ChartMarkerStyle) => void;
};

const iconWithLine = (el: ReactElement | null) => (
    <Relative style={{ width: 24, height: 24 }}>
        <IsolateAbsolute>
            <Line />
        </IsolateAbsolute>
        {el ? <IsolateAbsolute>{el}</IsolateAbsolute> : null}
    </Relative>
);

const MARKER_ICONS = {
    [ChartMarkerStyle.None]: iconWithLine(null),
    [ChartMarkerStyle.Dot]: iconWithLine(<Dot />),
    [ChartMarkerStyle.Circle]: iconWithLine(<Circle />),
    [ChartMarkerStyle.Square]: iconWithLine(<Square />),
} as Record<ChartMarkerStyle, ReactElement>;

export const MarkerStylePicker = (props: MarkerStylePickerProps) => {
    const { disabled, value, onChange } = props;

    const { LL } = useI18nContext();

    const buttonRef = useRef<HTMLButtonElement>(null);
    const { menuState, menuHandlers } = useContextMenuState({ click: 'left' });

    const icon = MARKER_ICONS[value];

    return (
        <>
            <Button
                {...menuHandlers}
                icon={icon}
                ref={buttonRef}
                disabled={disabled}
                $type="quaternary"
                $size="small"
            />
            <ContextMenu {...menuState} anchorRef={buttonRef} placement="bottom-start">
                <ContextMenu.Item
                    item={{
                        label: LL.NONE(),
                        icon: () => MARKER_ICONS[ChartMarkerStyle.None],
                        action: () => onChange?.(ChartMarkerStyle.None),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.DOT(),
                        icon: () => MARKER_ICONS[ChartMarkerStyle.Dot],
                        action: () => onChange?.(ChartMarkerStyle.Dot),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.CIRCLE(),
                        icon: () => MARKER_ICONS[ChartMarkerStyle.Circle],
                        action: () => onChange?.(ChartMarkerStyle.Circle),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.SQUARE(),
                        icon: () => MARKER_ICONS[ChartMarkerStyle.Square],
                        action: () => onChange?.(ChartMarkerStyle.Square),
                    }}
                />
            </ContextMenu>
        </>
    );
};
